import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, ChevronDown, User, Clock, X, GraduationCap } from 'lucide-react';
import { formatCourseDate, formatSemesterLabel } from '../../utils/dateFormatter';

// Hook to detect if we're on mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export default function EmailContentCard({ email, onView }) {
  const { t } = useTranslation('planner');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hasMarkedAsViewed, setHasMarkedAsViewed] = useState(false);
  const isMobile = useIsMobile();

  // Derive shouldRender from isModalOpen or isClosing
  const shouldRender = isModalOpen || isClosing;

  // Derive isUnviewed from server state and local optimistic state
  const isUnviewed = !email.viewed_at && !hasMarkedAsViewed;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (shouldRender && !isClosing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldRender, isClosing]);

  // Mark email as viewed - called when opening
  const markAsViewed = useCallback(() => {
    if (isUnviewed && onView) {
      onView(email.id);
      setHasMarkedAsViewed(true);
    }
  }, [isUnviewed, onView, email.id]);

  const handleToggle = () => {
    if (isMobile) {
      setIsModalOpen(true);
      markAsViewed();
    } else {
      const newExpanded = !isExpanded;
      setIsExpanded(newExpanded);
      if (newExpanded) {
        markAsViewed();
      }
    }
  };

  const handleCloseModal = useCallback(() => {
    setIsClosing(true);
  }, []);

  // Handle animation end to complete the close
  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      setIsModalOpen(false);
      setIsClosing(false);
    }
  }, [isClosing]);

  if (!email) {
    return null;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border mb-4 overflow-hidden ${isUnviewed ? 'border-[#589DD5] ring-2 ring-[#589DD5]/20' : 'border-gray-100 dark:border-gray-700'}`}>
      <button
        onClick={handleToggle}
        className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isUnviewed ? 'bg-[#589DD5] text-white' : 'bg-blue-50 dark:bg-blue-900/30 text-[#589DD5]'}`}>
            <Mail className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                {t('email.title')}
              </h3>
              {isUnviewed ? (
                <span className="px-2 py-0.5 bg-[#589DD5] text-white text-xs font-medium rounded-full">
                  {t('email.new')}
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium rounded-full">
                  {t('email.read')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {email.semester_label && (
                <span className="flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  {formatSemesterLabel(email.semester_label)}
                </span>
              )}
              {email.author && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {email.author}
                </span>
              )}
              {email.created_at && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatCourseDate(email.created_at)}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* Chevron - only show on desktop */}
        <div className={`shrink-0 w-8 h-8 rounded-full items-center justify-center transition-colors hidden lg:flex ${
          isExpanded ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expanded content with smooth animation - desktop only */}
      <div className="email-accordion hidden lg:block" data-open={isExpanded}>
        <div className="accordion-content">
          <div className="px-4 pb-4 sm:px-5 sm:pb-5">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 email-content"
                dangerouslySetInnerHTML={{ __html: email.email_content }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Modal */}
      {shouldRender && (
        <div className="fixed inset-0 z-9999 lg:hidden flex flex-col justify-end">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
              isClosing ? 'opacity-0' : 'opacity-100'
            }`}
            onClick={handleCloseModal}
            onTransitionEnd={handleAnimationEnd}
          />
          {/* Bottom Sheet */}
          <div className={`relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col transition-transform duration-300 ease-out ${
            isClosing ? 'translate-y-full' : 'animate-slide-up'
          }`}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>

            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-[#589DD5]">
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                    {t('email.title')}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    {email.semester_label && (
                      <span className="flex items-center gap-1">
                        <GraduationCap className="w-3 h-3" />
                        {formatSemesterLabel(email.semester_label)}
                      </span>
                    )}
                    {email.author && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {email.author}
                      </span>
                    )}
                    {email.created_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatCourseDate(email.created_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-4">
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
                <div
                  className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 email-content"
                  dangerouslySetInnerHTML={{ __html: email.email_content }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseModal}
                className="w-full py-3 bg-[#589DD5] text-white font-medium rounded-xl hover:bg-[#4a8bc4] transition-colors"
              >
                {t('email.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
